"use client";

import React from "react";
import { ArrowRight, ShieldCheck, Flame, Scale } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
const solutions = [{
  id: "safety-security",
  title: "Safety & Security Solutions",
  badge: "Field Identification",
  icon: Flame,
  path: "/SafetyAndSecurityPage",
  description: "Rapid on-site chemical identification and hazardous material screening for first responders, hazmat teams, and environmental safety officers using rugged handheld Raman and NIR instrumentation.",
  specs: ["Hazmat Screening", "Toxic Chemical Identification", "Explosives Detection"]
}, {
  id: "law-enforcement",
  title: "Law Enforcement & Public Safety",
  badge: "Forensics & Narcotics",
  icon: Scale,
  path: "/LawEnforcementPublicSafetyPage",
  description: "Court-admissible non-destructive illicit substance identification and evidence screening through translucent packaging, reducing officer exposure to high-potency opioids and narcotics.",
  specs: ["Narcotics & Precursors", "Non-Destructive Testing", "Digital Audit Trail"]
}, {
  id: "border-defence",
  title: "Border Security & Defence",
  badge: "CBRNe & Customs",
  icon: ShieldCheck,
  path: "/BorderSecurityAndDefencePage",
  description: "High-throughput checkpoint inspection, CBRNe threat detection, and military-grade optical spectroscopy systems engineered for stringent border control and defense operations.",
  specs: ["CBRNe Threat Detection", "Customs Interdiction", "MIL-SPEC Environmental Compliance"]
}];
export default function SolutionsOverview() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="solutionsoverview" data-nav="light" className="py-20 lg:py-28 bg-background border-b border-border">
      <div className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* 4-column left overview */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-4 flex flex-col items-start gap-6 lg:sticky lg:top-28">
            <Badge variant="secondary" className="px-3 py-1 font-medium">
              Analytical Capabilities
            </Badge>

            <div className="space-y-4">
              <h2 className="font-default text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
                Targeted analytical solutions for critical environments.
              </h2>
              <p className="font-sans text-base text-muted-foreground leading-relaxed">
                Application-led analytical technology and rugged spectroscopic instrumentation
                engineered to deliver fast, unambiguous identification in frontline security,
                policing, and defence operations across Ireland and the UK.
              </p>
            </div>

            <div className="pt-2 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <Button asChild variant="default" size="lg" className="w-full sm:w-auto">
                <Link to="/SolutionsPage">
                  <span>View All Solutions</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <Link to="/DiscussYourApplicationPage">
                  <span>Discuss Application</span>
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* 8-column right solutions list */}
          <div className="lg:col-span-8 flex flex-col gap-6">
            {solutions.map((item, i) => {
            const IconComponent = item.icon;
            return <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.45,
              delay: i * 0.12,
              ease: "easeOut"
            }}>
                  <Card className="group relative border border-border bg-card hover:border-primary/50 transition-colors duration-200 overflow-hidden">
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 rounded-md bg-secondary text-secondary-foreground">
                            <IconComponent className="size-5 text-primary" />
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {item.badge}
                          </Badge>
                        </div>
                        <Link to={item.path} className="inline-flex items-center justify-center size-9 rounded-md bg-secondary/80 text-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200" aria-label={`Learn more about ${item.title}`}>
                          <ArrowRight className="size-4" />
                        </Link>
                      </div>
                      <CardTitle className="font-default text-xl sm:text-2xl pt-3 text-card-foreground">
                        <Link to={item.path} className="hover:underline">
                          {item.title}
                        </Link>
                      </CardTitle>
                      <CardDescription className="font-sans text-sm sm:text-base leading-relaxed text-muted-foreground pt-1">
                        {item.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-0 pb-6">
                      <div className="flex flex-wrap gap-2">
                        {item.specs.map((spec, specIdx) => <span key={spec} data-index={specIdx} className="inline-flex items-center text-xs font-medium px-2.5 py-1 rounded bg-muted text-muted-foreground">
                            {spec}
                          </span>)}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}